import { ALL_COUNTRY_CODES, type CountryCode } from "./countries";
import { USER_SELECTABLE_CURRENCY_CODES } from "./currencies";

export const VIEWER_LOCALES = ["en", "pt", "es"] as const;
export type ViewerLocale = (typeof VIEWER_LOCALES)[number];
export const DEFAULT_VIEWER_LOCALE: ViewerLocale = "pt";
export type ViewerCurrencyCode = (typeof USER_SELECTABLE_CURRENCY_CODES)[number];
export const DEFAULT_VIEWER_CURRENCY: ViewerCurrencyCode = USER_SELECTABLE_CURRENCY_CODES[0];
export const DEFAULT_VIEWER_COUNTRY = "BR";
export const DEFAULT_VIEWER_TIME_ZONE = "UTC";

export const LEGACY_VIEWER_LOCALE_MAP: Record<string, ViewerLocale> = {
  "pt-BR": "pt",
  "pt-br": "pt",
};

const VIEWER_LOCALE_SET = new Set<string>(VIEWER_LOCALES);
const VIEWER_COUNTRY_SET = new Set<string>(ALL_COUNTRY_CODES);
const VIEWER_CURRENCY_SET = new Set<string>(USER_SELECTABLE_CURRENCY_CODES);

const COUNTRY_CURRENCY_MAP: Partial<Record<CountryCode, string>> = {
  AD: "EUR",
  AT: "EUR",
  BE: "EUR",
  BR: "BRL",
  CY: "EUR",
  DE: "EUR",
  EE: "EUR",
  ES: "EUR",
  FI: "EUR",
  FR: "EUR",
  GR: "EUR",
  IE: "EUR",
  IT: "EUR",
  LT: "EUR",
  LU: "EUR",
  LV: "EUR",
  MC: "EUR",
  MT: "EUR",
  NL: "EUR",
  PT: "EUR",
  SI: "EUR",
  SK: "EUR",
  SM: "EUR",
  US: "USD",
  VA: "EUR",
  XE: "EUR",
};

const COUNTRY_LOCALE_MAP: Partial<Record<CountryCode, ViewerLocale>> = {
  AR: "es",
  BO: "es",
  BR: "pt",
  CL: "es",
  CO: "es",
  CR: "es",
  CU: "es",
  DO: "es",
  EC: "es",
  ES: "es",
  GT: "es",
  HN: "es",
  MX: "es",
  NI: "es",
  PA: "es",
  PE: "es",
  PT: "pt",
  PY: "es",
  SV: "es",
  UY: "es",
  VE: "es",
};

export interface ViewerPreferences {
  locale: ViewerLocale;
  currency: ViewerCurrencyCode;
  country: CountryCode;
  timeZone: string;
}

export function parseViewerLocale(value: unknown): ViewerLocale | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const legacy =
    LEGACY_VIEWER_LOCALE_MAP[trimmed] ?? LEGACY_VIEWER_LOCALE_MAP[trimmed.toLowerCase()];
  const normalized = legacy ?? trimmed.toLowerCase();
  return VIEWER_LOCALE_SET.has(normalized) ? (normalized as ViewerLocale) : null;
}

export function normalizeViewerLocale(value: unknown): ViewerLocale {
  return parseViewerLocale(value) ?? DEFAULT_VIEWER_LOCALE;
}

export function normalizeViewerCurrency(value: unknown): ViewerCurrencyCode | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toUpperCase();
  return VIEWER_CURRENCY_SET.has(normalized) ? (normalized as ViewerCurrencyCode) : null;
}

export function normalizeViewerCountry(value: unknown): CountryCode | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toUpperCase();
  return VIEWER_COUNTRY_SET.has(normalized) ? (normalized as CountryCode) : null;
}

export function normalizeViewerTimeZone(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function readCookieValue(
  cookieHeader: string | null | undefined,
  name: string,
): string | null {
  const prefix = `${name}=`;
  const match = cookieHeader
    ?.split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));

  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
}

export function resolveViewerCurrency(input: {
  userCurrency?: string | null;
  cookieCurrency?: string | null;
  country?: string | null;
  defaultCurrency?: string;
}): ViewerCurrencyCode {
  return (
    normalizeViewerCurrency(input.userCurrency) ??
    normalizeViewerCurrency(input.cookieCurrency) ??
    currencyForCountry(input.country) ??
    normalizeViewerCurrency(input.defaultCurrency) ??
    DEFAULT_VIEWER_CURRENCY
  );
}

export function resolveViewerCountry(input: {
  edgeCountry?: string | null;
  userCountry?: string | null;
  defaultCountry?: string;
}): CountryCode {
  return (
    normalizeViewerCountry(input.edgeCountry) ??
    normalizeViewerCountry(input.userCountry) ??
    normalizeViewerCountry(input.defaultCountry) ??
    DEFAULT_VIEWER_COUNTRY
  );
}

export function resolveViewerTimeZone(input: {
  cookieTimeZone?: string | null;
  edgeTimeZone?: string | null;
  defaultTimeZone?: string;
}): string {
  return (
    normalizeViewerTimeZone(input.cookieTimeZone) ??
    normalizeViewerTimeZone(input.edgeTimeZone) ??
    input.defaultTimeZone ??
    DEFAULT_VIEWER_TIME_ZONE
  );
}

export function currencyForCountry(country: unknown): ViewerCurrencyCode | null {
  const normalized = normalizeViewerCountry(country);
  if (!normalized) return null;
  return normalizeViewerCurrency(COUNTRY_CURRENCY_MAP[normalized]) ?? DEFAULT_VIEWER_CURRENCY;
}

export function localeForCountry(country: unknown): ViewerLocale | null {
  const normalized = normalizeViewerCountry(country);
  if (!normalized) return null;
  return COUNTRY_LOCALE_MAP[normalized] ?? null;
}

export function countryFromLocale(value: unknown): CountryCode | null {
  if (typeof value !== "string") return null;

  for (const token of value.split(",")) {
    const [locale] = token.trim().split(";q=");
    const region = locale?.split(/[-_]/)[1];
    const normalized = normalizeViewerCountry(region);
    if (normalized) return normalized;
  }

  return null;
}

export function resolveViewerLocale(input: {
  userLanguage?: string | null;
  cookieLocale?: string | null;
  acceptLanguage?: string | null;
  country?: string | null;
}): ViewerLocale {
  return (
    parseViewerLocale(input.userLanguage) ??
    parseViewerLocale(input.cookieLocale) ??
    resolveFromAcceptLanguage(input.acceptLanguage) ??
    localeForCountry(input.country) ??
    DEFAULT_VIEWER_LOCALE
  );
}

export function resolveViewerPreferences(input: {
  userLanguage?: string | null;
  userIsAnonymous?: boolean;
  cookieLocale?: string | null;
  acceptLanguage?: string | null;
  userCurrency?: string | null;
  cookieCurrency?: string | null;
  edgeCountry?: string | null;
  userCountry?: string | null;
  cookieTimeZone?: string | null;
  edgeTimeZone?: string | null;
  defaultCountry?: string;
  defaultCurrency?: string;
  defaultTimeZone?: string;
}): ViewerPreferences {
  const detectedCountry =
    normalizeViewerCountry(input.edgeCountry) ?? normalizeViewerCountry(input.userCountry);
  const country = resolveViewerCountry({
    edgeCountry: input.edgeCountry,
    userCountry: input.userCountry,
    defaultCountry: input.defaultCountry,
  });

  return {
    locale: resolveViewerLocale({
      // Anonymous users are durable guest identities, but their default
      // database language must not override an explicit browser preference.
      userLanguage: input.userIsAnonymous ? null : input.userLanguage,
      cookieLocale: input.cookieLocale,
      acceptLanguage: input.acceptLanguage,
      country: detectedCountry,
    }),
    currency: resolveViewerCurrency({
      userCurrency: input.userCurrency,
      cookieCurrency: input.cookieCurrency,
      country: detectedCountry,
      defaultCurrency: input.defaultCurrency,
    }),
    country,
    timeZone: resolveViewerTimeZone({
      cookieTimeZone: input.cookieTimeZone,
      edgeTimeZone: input.edgeTimeZone,
      defaultTimeZone: input.defaultTimeZone,
    }),
  };
}

function resolveFromAcceptLanguage(header: string | null | undefined): ViewerLocale | null {
  if (!header) return null;

  const preferred = header
    .split(",")
    .map((part) => {
      const [lang, q] = part.trim().split(";q=");
      const quality = q ? Number.parseFloat(q) : 1.0;
      return {
        lang: lang?.trim() ?? "",
        quality: Number.isFinite(quality) ? quality : 1.0,
      };
    })
    .filter(({ lang, quality }) => lang.length > 0 && quality > 0)
    .sort((a, b) => b.quality - a.quality);

  for (const { lang } of preferred) {
    const exact = parseViewerLocale(lang);
    if (exact) return exact;

    const prefix = lang.split(/[-_]/)[0]?.toLowerCase();
    if (prefix && VIEWER_LOCALE_SET.has(prefix)) {
      return prefix as ViewerLocale;
    }
  }

  return null;
}
